import {
  AlertTriangle, CheckCircle2, Brain, Package, ClipboardCheck, FileEdit, FileText, FlaskConical, HelpCircle, ListChecks, MoveRight, Play,
  Search, Terminal, Trash2, XCircle, CircleDot, RotateCcw,
} from "lucide-react";
import { useTaal } from "@/contexts/TaalContext";
import { detailInTaal, stapInTaal } from "@/lib/codestappen-taal";
import { geleerdeRegel } from "@/lib/werkruimte-tekst";

/**
 * What the agent did, one line per step, in the order it happened.
 *
 * The same list the panel already drew for a single run. That was the point of
 * building it as a step list before the loop existed: the loop adds kinds of
 * step, not a new screen.
 *
 * A step that needs the person is not a row like the others. `ask` and
 * `stopped` are drawn as a block that says so, because a loop that quietly
 * ended in the middle of a list reads as a loop that finished.
 *
 * A check that could not be made is drawn the same way, and that is the point
 * of it. The run finished and nobody confirmed the result: a muted grey line
 * would read as a detail, and this is not a detail. Frank's rule of 28 July -
 * never a silent pass, the label is the honesty.
 */
const ICONS = {
  task: Play,
  memory: Brain,
  write: FileEdit,
  read: FileText,
  run: Terminal,
  // LC3 (31 okt 2026).
  plan: ListChecks,
  test: FlaskConical,
  // LC4.
  search: Search,
  // CA1.
  delete: Trash2,
  move: MoveRight,
  check: ClipboardCheck,
  problem: AlertTriangle,
  ask: HelpCircle,
  done: CheckCircle2,
  // GL223-2: de afloop van een installatie.
  install: Package,
  // CA8.
  issue: CircleDot,
  stopped: XCircle,
  crash: XCircle,
  // Glasronde 0.9.231 punt 3: grijs, want het herstel werkte.
  herkans: RotateCcw,
};

export default function AgentSteps({ steps, outcome }) {
  // GL216 punt 2: de titels komen Nederlands uit de agentlus; hier in de taal van de app.
  const { t, taal } = useTaal();
  // Glasronde 0.9.221 punt 3: wat de agent leerde staat opgeslagen in de zin voor het model; op het scherm in de taal
  // van de app, net als in het paneel Geleerd (GL220-11, werkruimte-tekst.js).
  // GL222-2: ook "Gebruikt wat deze werkruimte eerder leerde", een geleerde zin per regel.
  const detail = (step) => (step.kind === "memory"
    ? String(step.detail || "").split("\n").map((regel) => geleerdeRegel(regel, t)).join("\n")
    : detailInTaal(step.detail, taal));
  if (!steps.length) return null;
  return (
    <ol className="space-y-1.5" data-testid="agent-steps">
      {steps.map((step) => {
        const Icon = ICONS[step.kind] || Play;
        const unchecked = step.kind === "check" && step.ok !== true && step.ok !== false;
        const wants = step.kind === "ask" || step.kind === "stopped" || step.kind === "crash" || unchecked;
        return (
          <li key={step.id} data-testid={`agent-step-${step.kind}`}>
            {wants ? (
              <div className="rounded-lg border border-corona/40 bg-corona/10 p-3">
                <div className="flex items-start gap-2">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-corona" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{stapInTaal(step.title, taal)}</p>
                    {step.detail && (
                      <p className="mt-1 break-words text-sm text-muted-foreground">{detail(step)}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-sm">
                <Icon
                  className={`mt-0.5 h-4 w-4 shrink-0 ${
                    step.kind === "done" || (step.kind === "check" && step.ok === true)
                      ? "text-ok"
                      : step.kind === "problem" || step.ok === false
                        ? "text-destructive"
                        : "text-muted-foreground"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  {/* min-w-0 and break-words, or a traceback line pushes the
                      row off the phone. */}
                  <p className="break-words">{stapInTaal(step.title, taal)}</p>
                  {step.detail && (
                    <p className="break-words text-xs text-muted-foreground">{detail(step)}</p>
                  )}
                </div>
              </div>
            )}
          </li>
        );
      })}
      {outcome && (
        <li className="pt-1 text-xs text-muted-foreground" data-testid="agent-outcome">
          {outcome}
        </li>
      )}
    </ol>
  );
}
